import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import useDebounce from 'Hooks/useDebounce';
import { buildBulkApproveSchema, type BulkApproveFormValues } from './schema';

interface Props {
  open: boolean;
  priorityId: string;
  currentUserCourseId: string | null;
}

/**
 * Encapsula el estado del modal: form, panel expandido y filtros del DataGrid.
 * El reset ocurre cuando el modal se abre con un curso distinto (changes a `currentUserCourseId`).
 */
const useBulkApproveForm = ({ open, priorityId, currentUserCourseId }: Props) => {
  const schema = useMemo(() => buildBulkApproveSchema(priorityId), [priorityId]);

  const form = useForm<BulkApproveFormValues>({
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    resolver: zodResolver(schema),
    defaultValues: {
      evaluationDate: new Date(),
      evaluationDateTouched: false,
      file: null,
      selectedUserCourseIds: currentUserCourseId ? [currentUserCourseId] : [],
    },
  });

  const [expanded, setExpanded] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(15);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  // Reset al abrir/cerrar o cambiar de curso.
  useEffect(() => {
    if (!open) return;
    form.reset({
      evaluationDate: new Date(),
      evaluationDateTouched: false,
      file: null,
      selectedUserCourseIds: currentUserCourseId ? [currentUserCourseId] : [],
    });
    setExpanded(false);
    setPage(0);
    setPageSize(15);
    setSearch('');
  }, [open, currentUserCourseId]);

  // Al cambiar el filtro de búsqueda, regresar a la primera página para evitar
  // resultados vacíos por estar en una página fuera del nuevo total.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  return {
    form,
    expanded,
    setExpanded,
    page,
    setPage,
    pageSize,
    setPageSize,
    search,
    setSearch,
    debouncedSearch,
  };
};

export default useBulkApproveForm;
